import React, { useEffect, useMemo } from "react";
import { FaStar, FaRegStar } from "react-icons/fa";
import VideoBox from "./VideoBox";
import { Link } from "./usePath";
import { computeRank } from "./utils";

const DIVISION_LABEL = { M: "Men's", W: "Women's" };

function VideoPage({ videoId, allVideos, loading }) {
  const video = useMemo(
    () => allVideos.find((v) => v.id === videoId),
    [allVideos, videoId]
  );

  const ranks = useMemo(() => {
    if (!video) return null;
    return {
      overall: computeRank(video, allVideos),
      division: computeRank(
        video,
        allVideos.filter((v) => v.division === video.division)
      ),
      year: computeRank(
        video,
        allVideos.filter((v) => v.year === video.year)
      ),
      triple: computeRank(
        video,
        allVideos.filter(
          (v) =>
            v.division === video.division &&
            v.year === video.year &&
            v.award === video.award
        )
      ),
      school: computeRank(
        video,
        allVideos.filter((v) => v.school === video.school)
      ),
      schoolDivision: computeRank(
        video,
        allVideos.filter(
          (v) => v.school === video.school && v.division === video.division
        )
      ),
    };
  }, [allVideos, video]);

  useEffect(() => {
    if (!video) return;
    const prev = document.title;
    document.title = `${video.name || "Video"} — callahanvideos`;
    return () => {
      document.title = prev;
    };
  }, [video]);

  if (loading || allVideos.length === 0) {
    return <div className="loading">Loading videos from database...</div>;
  }

  if (!video) {
    return (
      <div className="year-page">
        <h2>Video not found</h2>
        <p>
          <Link to="/">← Back to rankings</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="year-page">
      <h1 style={{ marginBottom: 4 }}>
        {video.name || "??"}{" "}
        {video.winner ? (
          <FaStar style={{ color: "gold", fontSize: 22 }} />
        ) : video.finalist ? (
          <FaRegStar style={{ color: "grey", fontSize: 18 }} />
        ) : null}
      </h1>
      <p style={{ marginTop: 0, color: "#555" }}>
        <Link to={`/school/${encodeURIComponent(video.school || "")}`}>
          {video.school || "Unknown school"}
        </Link>
        {" · "}
        {video.year}
        {" · "}
        {DIVISION_LABEL[video.division] || video.division || "?"}
        {" · "}
        {video.award}
      </p>

      <div className="video-container" style={{ justifyContent: "center" }}>
        {video.videoId ? (
          <VideoBox video={video} showInfo={false} />
        ) : (
          <div className="video-column">
            <div
              className="video-wrapper"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#eee",
                color: "#666",
              }}
            >
              Video unavailable
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          maxWidth: 480,
          margin: "20px auto",
          padding: "16px",
          border: "1px solid #ddd",
          borderRadius: 12,
          background: "#fafafa",
        }}
      >
        <h3 style={{ marginTop: 0 }}>Stats</h3>
        <BoldRow value={video.elo ?? 1000} suffix="ELO" />
        <RankRow rank={ranks.overall} suffix="overall" />
        <RankRow
          rank={ranks.division}
          suffix={`in ${DIVISION_LABEL[video.division] || video.division}`}
        />
        <RankRow rank={ranks.year} suffix={`in ${video.year}`} />
        <RankRow
          rank={ranks.triple}
          suffix={`in ${
            DIVISION_LABEL[video.division] || video.division
          } ${video.award} ${video.year}`}
        />
        <RankRow
          rank={ranks.school}
          suffix={`at ${video.school || "school"}`}
        />
        <RankRow
          rank={ranks.schoolDivision}
          suffix={`in ${
            DIVISION_LABEL[video.division] || video.division
          } at ${video.school || "school"}`}
        />
      </div>

      <p style={{ textAlign: "center" }}>
        <Link to="/">← Back to rankings</Link>
      </p>
    </div>
  );
}

const BoldRow = ({ value, suffix }) => (
  <div
    style={{
      padding: "6px 0",
      borderBottom: "1px solid #eee",
    }}
  >
    <span style={{ fontWeight: 600 }}>{value}</span>{" "}
    <span style={{ color: "#555" }}>{suffix}</span>
  </div>
);

const RankRow = ({ rank, suffix }) => (
  <div
    style={{
      padding: "6px 0",
      borderBottom: "1px solid #eee",
    }}
  >
    <span style={{ fontWeight: 600 }}>
      #{rank.rank} of {rank.total}
    </span>{" "}
    <span style={{ color: "#555" }}>{suffix}</span>
  </div>
);

export default VideoPage;
